import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { createTemplate } from '@/lib/api/tournament-templates';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import type { TournamentFormController } from './useTournamentFormController';

// Story 078 — "Save as template" affordance. Independent of the
// tournament Save button: pressing it captures the current form
// values into a named template, does not submit the tournament.
// 409 from the API switches the dialog into an overwrite-confirm
// state instead of failing outright.

interface Props {
  form: TournamentFormController;
}

type Phase =
  | { kind: 'name' }
  | { kind: 'confirm-overwrite' }
  | { kind: 'saving' };

export function SaveTemplateButton({ form }: Props) {
  const { t } = useTranslation('director');
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'name' });

  function reset() {
    setOpen(false);
    setName('');
    setPhase({ kind: 'name' });
  }

  async function save(overwrite: boolean) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setPhase({ kind: 'saving' });
    try {
      await createTemplate({
        name: trimmed,
        values: form.captureTemplateValues(),
        overwrite,
      });
      showSuccessToast(t('tournament_form.save_template.saved_toast'));
      reset();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && !overwrite) {
        setPhase({ kind: 'confirm-overwrite' });
        return;
      }
      console.error('Save template failed', err);
      showErrorToast(
        err instanceof ApiError
          ? err.userMessage
          : t('tournament_form.save_template.failed_toast'),
      );
      setPhase({ kind: 'name' });
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        data-testid="save-as-template-button"
        onClick={() => setOpen(true)}
      >
        {t('tournament_form.save_template.button')}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) reset();
          else setOpen(true);
        }}
      >
        <DialogContent data-testid="save-template-dialog">
          {phase.kind !== 'confirm-overwrite' ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('tournament_form.save_template.dialog_title')}
                </DialogTitle>
                <DialogDescription>
                  {t('tournament_form.save_template.dialog_description')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="template-name">
                  {t('tournament_form.save_template.name_label')}
                </Label>
                <Input
                  id="template-name"
                  data-testid="template-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t(
                    'tournament_form.save_template.name_placeholder',
                  )}
                  autoFocus
                  disabled={phase.kind === 'saving'}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="save-template-cancel-button"
                  onClick={reset}
                  disabled={phase.kind === 'saving'}
                >
                  {t('tournament_form.save_template.cancel')}
                </Button>
                <Button
                  type="button"
                  data-testid="save-template-confirm-button"
                  onClick={() => save(false)}
                  disabled={!name.trim() || phase.kind === 'saving'}
                >
                  {t('tournament_form.save_template.save')}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div data-testid="save-template-overwrite-dialog">
              <DialogHeader>
                <DialogTitle>
                  {t('tournament_form.save_template.overwrite_title')}
                </DialogTitle>
                <DialogDescription>
                  {t('tournament_form.save_template.overwrite_description', {
                    name: name.trim(),
                  })}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="save-template-overwrite-cancel-button"
                  onClick={() => setPhase({ kind: 'name' })}
                >
                  {t('tournament_form.save_template.cancel')}
                </Button>
                <Button
                  type="button"
                  data-testid="save-template-overwrite-confirm-button"
                  onClick={() => save(true)}
                >
                  {t('tournament_form.save_template.overwrite_confirm')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
